import { action, observable } from 'mobx'
import { type IValidationSchema, required, validationContext } from 'validx'
import type { LegendPartDependencies } from '@/features/legends/editor/legend-part-dependencies'
import { LegendPartViewModelBase } from '@/features/legends/editor/LegendPartViewModelBase'
import {
  type ErrorDescriptor,
  receiveErrors,
} from '@/features/legends/editor/analyzer-errors'
import { newCid } from '@/features/legends/editor/collections'
import type { SymbolContext } from '@/features/legends/editor/entities'
import type { RoutingPlan } from '@/features/legends/routing/routing'
import { validateWhileEditing } from '@/features/legends/editor/validation'
import { FiltersBuilderViewModel } from '@/features/legends/filters/FiltersBuilderViewModel'
import type { FilterScope } from '@/features/legends/filters/filter-scope'
import { validateObject } from '@/utils/validx-util'

const MAX_DURATION_MINUTES = 72 * 60
const DEFAULT_DURATION_MINUTES = 5

/**
 * The tabs of a routing plan's editor.
 */
export type RoutingPlanTab = 'General' | 'Requirements'

/**
 * A routing plan: for a while, only pros that also match its requirements
 * can pick up the request.
 */
export class RoutingPlanEditorViewModel extends LegendPartViewModelBase {
  private readonly _schema: IValidationSchema<RoutingPlanEditorViewModel> = {
    label: [required('Label is required')],
    duration: [
      ({ value }) =>
        (Number(value) >= 1 && Number(value) <= MAX_DURATION_MINUTES) ||
        `Duration must be at least 1 minute and at most ${MAX_DURATION_MINUTES} minutes.`,
    ],
    requirements: [validateObject('There are invalid requirements.')],
  }

  /**
   * Identifies the plan on the client, and stems the ID generated for it.
   */
  readonly cid = newCid('routingplan')

  /**
   * The requirements pros must also match while the plan is active.
   */
  readonly requirements: FiltersBuilderViewModel

  /**
   * Empty until the plan is saved and reloaded: the document gets an ID
   * generated from the label instead, which follows the label until then.
   */
  @observable accessor id = ''
  @observable accessor label = ''
  @observable accessor description = ''

  /**
   * Minutes. The engine stores seconds.
   */
  @observable accessor duration = DEFAULT_DURATION_MINUTES

  readonly validation = validationContext(this, this._schema)

  constructor(
    dependencies: LegendPartDependencies,
    scope: readonly FilterScope[],
    context: SymbolContext,
  ) {
    super(dependencies)
    this.requirements = new FiltersBuilderViewModel({
      dependencies,
      scope,
      context,
    })
    this.lifecycle.manage(this.requirements)
    this.lifecycle.manage(validateWhileEditing(this, this._schema))
  }

  /**
   * The first validation error of the fields on the tab, if any.
   */
  getTabError(tab: RoutingPlanTab) {
    const v = this.validation
    return tab === 'General'
      ? (v.getError('label') ??
          v.getError('description') ??
          v.getError('duration'))
      : v.getError('requirements')
  }

  /**
   * Sets the plan from the legend document.
   */
  @action
  hydrate(plan: RoutingPlan) {
    this.requirements.hydrate(plan.requirements)
    this.id = plan.id ?? ''
    this.label = plan.label ?? ''
    this.description = plan.description ?? ''
    this.duration = Math.round(
      (plan.duration ?? DEFAULT_DURATION_MINUTES * 60) / 60,
    )
  }

  /**
   * Shows the analyzer's errors on the plan.
   */
  receiveErrors(descriptor: ErrorDescriptor) {
    receiveErrors(this, descriptor.inner)
  }

  /**
   * The plan as the legend document stores it.
   */
  toJSON(): RoutingPlan {
    return {
      id: this.id || this.dependencies.symbols.newId(this.cid, this.label),
      label: this.label,
      description: this.description,
      requirements: this.requirements.toJSON(),
      duration: this.duration * 60,
    }
  }
}
